/* ============================================================================
   Beeglee / Nisho — custom theme features for Tubonext Final
   Adds: dynamic cart cross-sell, localStorage wishlist (button + count + panel),
         cart<->wishlist drawer tabs.
   Size pills (E) and collection sub-nav (D) are pure Liquid/CSS — no JS needed.
   ========================================================================== */
(() => {
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const HEART_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

  /* ---------- shared money formatter (cents -> localized currency) -------- */
  const formatMoney = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  /* ======================================================================
     WISHLIST STORE (localStorage)
     ====================================================================== */
  const WL_KEY = 'theme:wishlist';
  const wlRead = () => {
    try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch (e) { return []; }
  };
  const wlWrite = (arr) => {
    try { localStorage.setItem(WL_KEY, JSON.stringify(arr)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: arr } }));
  };
  const wlHas = (handle) => wlRead().some((i) => i.handle === handle);
  const wlToggle = (handle, variantId) => {
    if (!handle) return false;
    const arr = wlRead();
    const idx = arr.findIndex((i) => i.handle === handle);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push({ handle, variantId: variantId || null, addedAt: Date.now() });
    wlWrite(arr);
    return wlHas(handle);
  };

  /* ---------- <wishlist-button> : heart toggle on cards / PDP ----------- */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('handle');
      this.variantId = this.getAttribute('variant-id');
      this._click = (e) => { e.preventDefault(); e.stopPropagation(); wlToggle(this.handle, this.variantId); };
      this._sync = () => this.reflect();
      this.addEventListener('click', this._click);
      document.addEventListener('wishlist:change', this._sync);
      this.reflect();
    }
    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this._sync);
    }
    reflect() {
      const on = wlHas(this.handle);
      this.classList.toggle('is-active', on);
      const btn = this.querySelector('button') || this;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }
  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

  /* ---------- <wishlist-count> : header badge --------------------------- */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._u = () => this.update();
      document.addEventListener('wishlist:change', this._u);
      this.update();
    }
    disconnectedCallback() { document.removeEventListener('wishlist:change', this._u); }
    update() {
      const n = wlRead().length;
      this.textContent = n > 0 ? n : '';
      this.toggleAttribute('hidden', n === 0);
    }
  }
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);

  /* ---------- <wishlist-panel> : renders saved items in the drawer ------ */
  class WishlistPanel extends HTMLElement {
    connectedCallback() {
      this.listEl = this.querySelector('[data-wishlist-list]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this._dirty = true;
      this._onChange = () => { this._dirty = true; if (!this.closest('[hidden]')) this.render(); };
      document.addEventListener('wishlist:change', this._onChange);
    }
    disconnectedCallback() { document.removeEventListener('wishlist:change', this._onChange); }

    async render() {
      if (!this._dirty || !this.listEl) return;
      this._dirty = false;
      const items = wlRead();
      if (this.emptyEl) this.emptyEl.toggleAttribute('hidden', items.length > 0);
      this.listEl.innerHTML = '';
      if (!items.length) return;

      const results = await Promise.all(items.map(async (it) => {
        try {
          const res = await fetch(`${root()}products/${it.handle}.js`);
          if (!res.ok) return null;
          return { product: await res.json(), saved: it };
        } catch (e) { return null; }
      }));

      results.forEach((r) => {
        if (r) this.listEl.insertAdjacentHTML('beforeend', this.cardHTML(r.product, r.saved));
      });
    }

    cardHTML(p, saved) {
      const img = (p.featured_image || (p.images && p.images[0]) || '');
      const imgTag = img
        ? `<img src="${img.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')}" alt="" loading="lazy" class="horizontal-product-card__image">`
        : '';

      const stored = saved.variantId && p.variants.find((v) => String(v.id) === String(saved.variantId));
      const variant = stored || p.variants.find((v) => v.available) || p.variants[0];
      const singleOrChosen = p.variants.length === 1 || !!stored;
      const addable = variant && variant.available;

      let action;
      if (singleOrChosen && addable) {
        action = `
          <product-form>
            <form action="${root()}cart/add" method="post" enctype="multipart/form-data">
              <input type="hidden" name="id" value="${variant.id}">
              <input type="hidden" name="quantity" value="1">
              <input type="hidden" name="on_success" value="force_open_drawer">
              <button type="submit" class="button button--outline button--subdued wishlist-card__add">Add to cart</button>
            </form>
          </product-form>`;
      } else if (!addable) {
        action = `<span class="wishlist-card__soldout text-subdued">Sold out</span>`;
      } else {
        action = `<a href="${p.url}" class="button button--outline button--subdued wishlist-card__add">Select options</a>`;
      }

      return `
        <div class="horizontal-product-card wishlist-card">
          <a href="${p.url}" class="horizontal-product-card__figure">${imgTag}</a>
          <div class="horizontal-product-card__info">
            <div class="v-stack gap-1 justify-items-start">
              <a href="${p.url}" class="product-title">${p.title}</a>
              <span class="price">${formatMoney(variant ? variant.price : p.price)}</span>
            </div>
            <div class="wishlist-card__actions">
              ${action}
              <wishlist-button handle="${p.handle}" class="wishlist-button wishlist-button--inline is-active" title="Remove from wishlist">
                <button type="button" class="wishlist-button__btn" aria-pressed="true" aria-label="Remove from wishlist">${HEART_SVG}</button>
              </wishlist-button>
            </div>
          </div>
        </div>`;
    }
  }
  if (!customElements.get('wishlist-panel')) customElements.define('wishlist-panel', WishlistPanel);

  /* ---------- <cart-wishlist-tabs> : the drawer toggle pill ------------- */
  class CartWishlistTabs extends HTMLElement {
    connectedCallback() {
      this.cartBtn = this.querySelector('[data-tab="cart"]');
      this.wlBtn = this.querySelector('[data-tab="wishlist"]');
      this.cartBtn && this.cartBtn.addEventListener('click', () => this.select('cart'));
      this.wlBtn && this.wlBtn.addEventListener('click', () => this.select('wishlist'));
      this._openWl = () => this.select('wishlist');
      document.addEventListener('wishlist:open', this._openWl);
      this.select('cart');
    }
    disconnectedCallback() { document.removeEventListener('wishlist:open', this._openWl); }
    select(which) {
      const drawer = this.closest('cart-drawer') || document.getElementById('cart-drawer');
      if (!drawer) return;
      const isCart = which === 'cart';
      const cartPanel = drawer.querySelector('[data-panel="cart"]');
      const wlPanel = drawer.querySelector('[data-panel="wishlist"]');
      cartPanel && cartPanel.toggleAttribute('hidden', !isCart);
      wlPanel && wlPanel.toggleAttribute('hidden', isCart);
      this.cartBtn && this.cartBtn.setAttribute('aria-selected', isCart ? 'true' : 'false');
      this.wlBtn && this.wlBtn.setAttribute('aria-selected', isCart ? 'false' : 'true');
      // checkout footer is only meaningful for the cart
      const footer = drawer.querySelector('[slot="footer"]');
      if (footer) footer.style.display = isCart ? '' : 'none';
      if (!isCart && wlPanel) {
        const panel = wlPanel.querySelector('wishlist-panel');
        panel && panel.render();
      }
    }
  }
  if (!customElements.get('cart-wishlist-tabs')) customElements.define('cart-wishlist-tabs', CartWishlistTabs);

  /* ======================================================================
     DYNAMIC CART CROSS-SELL  ("Add a little extra")
     Fetches complementary products for the first cart item, related fallback.
     ====================================================================== */
  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.listEl = this.querySelector('[data-cross-sell-list]');
      this.wrap = this.closest('[data-cross-sell-wrap]');
      this.load();
      this._onCart = (e) => {
        const items = e.detail && e.detail.cart && e.detail.cart.items;
        const pid = items && items[0] && items[0].product_id;
        if (pid && String(pid) !== this.getAttribute('data-product-id')) {
          this.setAttribute('data-product-id', String(pid));
          this.load();
        }
      };
      document.addEventListener('cart:change', this._onCart);
    }
    disconnectedCallback() { document.removeEventListener('cart:change', this._onCart); }

    async fetchList(intent) {
      const pid = this.getAttribute('data-product-id');
      const limit = this.getAttribute('data-limit') || 6;
      const section = this.getAttribute('data-section-id') || 'cart-recommendations';
      const url = `${root()}recommendations/products?product_id=${pid}&intent=${intent}&limit=${limit}&section_id=${section}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return doc.querySelector('[data-cross-sell-list]');
    }

    async load() {
      if (!this.getAttribute('data-product-id') || !this.listEl) { this.hide(); return; }
      try {
        let list = await this.fetchList('complementary');
        if (!list || !list.querySelector('.horizontal-product-card')) {
          list = await this.fetchList('related');
        }
        if (list && list.querySelector('.horizontal-product-card')) {
          this.listEl.replaceWith(list);
          this.listEl = list;
          this.show();
        } else {
          this.hide();
        }
      } catch (e) { this.hide(); }
    }
    show() { this.wrap && this.wrap.removeAttribute('hidden'); }
    hide() { this.wrap && this.wrap.setAttribute('hidden', ''); }
  }
  if (!customElements.get('cart-cross-sell')) customElements.define('cart-cross-sell', CartCrossSell);

  /* ---------- header heart: open drawer in wishlist view ---------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-wishlist-open]');
    if (trigger) document.dispatchEvent(new CustomEvent('wishlist:open'));
  });
})();
